import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Sparkle, Briefcase, Users, UserCircle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "../AuthContext";
import { useBranding } from "../useBranding";

const OPTIONS = [
  {
    key: "brand",
    icon: Briefcase,
    label: "Brand",
    tagline: "For brand teams running creator campaigns & ambassador programs.",
    accentColor: "#22D3EE",
    gradient: "from-[#22D3EE] to-[#8B5CF6]",
    href: "/login/brand",
    testid: "select-brand",
  },
  {
    key: "agency",
    icon: Users,
    label: "Agency",
    tagline: "For agencies executing campaigns across multiple client brands.",
    accentColor: "#A3E635",
    gradient: "from-[#A3E635] to-[#22D3EE]",
    href: "/login/agency",
    testid: "select-agency",
  },
  {
    key: "creator",
    icon: UserCircle,
    label: "Creator",
    tagline: "For creators & ambassadors — apply to drops, submit content, get paid.",
    accentColor: "#EC4899",
    gradient: "from-[#EC4899] to-[#8B5CF6]",
    href: "/login/creator",
    testid: "select-creator",
  },
];

export default function LoginSelector() {
  const { user, loading } = useAuth();
  const branding = useBranding();
  if (loading) return <div className="app-bg min-h-screen grid place-items-center text-muted">Loading…</div>;
  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/me";
    return <Navigate to={dest} replace />;
  }
  const appName = branding?.app_name || "CrewHQ";
  const accent = branding?.accent_color || "#3B82F6";
  return (
    <div className="app-bg min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-10" data-testid="login-selector">
      <div className="w-full max-w-5xl">
        {/* Brand strip */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2 group" data-testid="selector-logo">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={appName} className="w-9 h-9 rounded-xl object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
                <Sparkle size={16} weight="fill" color="white" />
              </div>
            )}
            <div className="text-white font-black tracking-tight" data-testid="brand-app-name">
              {appName.endsWith("HQ") && appName.includes("Crew") ? (
                <>{appName.replace("HQ", "")}<span style={{ color: accent }}>HQ</span></>
              ) : appName}
            </div>
          </Link>
          <Link to="/" className="text-xs text-muted hover:text-white flex items-center gap-1.5" data-testid="selector-back-home">
            <ArrowLeft size={12} weight="bold" /> Back to home
          </Link>
        </div>

        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#A3E635] flex items-center gap-2 justify-center">
            <Sparkle size={12} weight="fill" /> Sign in
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.02em] text-white leading-[1.05]">
            Choose your workspace
          </h1>
          <p className="mt-3 text-muted text-sm sm:text-base">
            Every role has its own dedicated console. Pick the one that fits.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-3">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <Link
                key={o.key}
                to={o.href}
                data-testid={o.testid}
                className="glass p-6 group hover-lift relative overflow-hidden flex flex-col"
              >
                <div className={`absolute -top-14 -right-14 w-40 h-40 rounded-full bg-gradient-to-br ${o.gradient} opacity-20 blur-3xl group-hover:opacity-50 transition`} />
                <div className="relative flex-1">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${o.gradient} grid place-items-center`}>
                    <Icon size={20} weight="bold" color="white" />
                  </div>
                  <div className="mt-5 text-white text-lg font-black">{o.label}</div>
                  <div className="mt-1 text-xs text-muted leading-relaxed">{o.tagline}</div>
                </div>
                <div className="relative mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-sm font-bold text-white">
                  Continue
                  <ArrowRight size={14} weight="bold" style={{ color: o.accentColor }} className="group-hover:translate-x-1 transition" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 text-center text-[11px] text-muted">
          Agencies use the same workspace as Brands under the hood — with agency-specific branding at login.
        </div>
      </div>
    </div>
  );
}
